"use client";

import { useId } from "react";
import { cn } from "@/lib/utils";

export interface VybeAgentMarkProps {
  /** Pixel width/height (square). Default 16. */
  size?: number;
  className?: string;
  thinking?: boolean;
  /** Match tinted agent chrome (e.g. avatar on primary/10). */
  tone?: "foreground" | "primary";
}

/** Union of pixel rects — same geometry as mark (shine only on glyph pixels). */
const LOGO_CLIP_RECTS = [
  { x: 15, y: 67, w: 30, h: 30 },
  { x: 15, y: 67, w: 40, h: 30 },
  { x: 35, y: 110, w: 80, h: 30 },
  { x: 95, y: 67, w: 140, h: 30 },
  { x: 55, y: 153, w: 40, h: 30 },
  { x: 145, y: 110, w: 40, h: 30 },
  { x: 145, y: 153, w: 40, h: 30 },
] as const;

/** Inline Vybe pixel mark from favicon geometry; theme via `dark:` (matches app theme toggle). */
export function VybeAgentMark({
  size = 16,
  className,
  thinking = false,
  tone = "foreground",
}: VybeAgentMarkProps) {
  const ink = thinking
    ? "fill-zinc-500 dark:fill-zinc-400"
    : tone === "primary"
      ? "fill-primary"
      : "fill-foreground";

  const uid = useId().replace(/:/g, "");
  const clipId = `vybe-agent-${uid}-clip`;
  const shineGradLightId = `vybe-agent-${uid}-shine-lt`;
  const shineGradDarkId = `vybe-agent-${uid}-shine-dk`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 250 250"
      width={size}
      height={size}
      className={cn("block shrink-0", className)}
      aria-hidden
    >
      {thinking ? (
        <defs>
          <clipPath id={clipId}>
            {LOGO_CLIP_RECTS.map((r, i) => (
              <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} />
            ))}
          </clipPath>
          <linearGradient
            id={shineGradLightId}
            gradientUnits="userSpaceOnUse"
            x1="-30"
            y1="125"
            x2="290"
            y2="125"
            gradientTransform="rotate(-10 125 125)"
          >
            <stop offset="0%" stopColor="#000000" stopOpacity={0} />
            <stop offset="4%" stopColor="#000000" stopOpacity={1} />
            <stop offset="38%" stopColor="#000000" stopOpacity={1} />
            <stop offset="43%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="50%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="57%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="62%" stopColor="#000000" stopOpacity={1} />
            <stop offset="96%" stopColor="#000000" stopOpacity={1} />
            <stop offset="100%" stopColor="#000000" stopOpacity={0} />
          </linearGradient>
          <linearGradient
            id={shineGradDarkId}
            gradientUnits="userSpaceOnUse"
            x1="-30"
            y1="125"
            x2="290"
            y2="125"
            gradientTransform="rotate(-10 125 125)"
          >
            <stop offset="0%" stopColor="#ffffff" stopOpacity={0} />
            <stop offset="4%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="38%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="43%" stopColor="#000000" stopOpacity={1} />
            <stop offset="50%" stopColor="#000000" stopOpacity={1} />
            <stop offset="57%" stopColor="#000000" stopOpacity={1} />
            <stop offset="62%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="96%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
          </linearGradient>
        </defs>
      ) : null}

      <g className="dark:hidden">
        <rect x="15" y="67" width="30" height="30" className={ink} />
        <rect x="15" y="67" width="40" height="30" className={ink} />
        <rect x="35" y="110" width="80" height="30" className={ink} />
        <rect x="95" y="67" width="140" height="30" className={ink} />
        <rect x="55" y="153" width="40" height="30" className={ink} />
        <rect x="145" y="110" width="40" height="30" className={ink} />
        <rect x="145" y="153" width="40" height="30" className={ink} />
      </g>
      <g className="hidden dark:block">
        <rect x="15" y="67" width="30" height="30" className="fill-background" />
        <rect x="15" y="67" width="40" height="30" className={ink} />
        <rect x="35" y="110" width="80" height="30" className={ink} />
        <rect x="95" y="67" width="140" height="30" className={ink} />
        <rect x="55" y="153" width="40" height="30" className={ink} />
        <rect x="145" y="110" width="40" height="30" className={ink} />
        <rect x="145" y="153" width="40" height="30" className={ink} />
      </g>

      {thinking ? (
        <>
          <g
            clipPath={`url(#${clipId})`}
            className="pointer-events-none dark:hidden [mix-blend-mode:hard-light] contrast-[1.45] saturate-[1.05]"
          >
            <rect
              x="-550"
              y="-80"
              width="1400"
              height="410"
              fill={`url(#${shineGradLightId})`}
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-420 0; 420 0; -420 0"
                keyTimes="0;0.5;1"
                dur="2.35s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.42 0 0.58 1 0.42 0 0.58 1"
              />
            </rect>
          </g>
          <g
            clipPath={`url(#${clipId})`}
            className="pointer-events-none hidden dark:block [mix-blend-mode:hard-light] contrast-[1.45] saturate-[1.05]"
          >
            <rect
              x="-550"
              y="-80"
              width="1400"
              height="410"
              fill={`url(#${shineGradDarkId})`}
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-420 0; 420 0; -420 0"
                keyTimes="0;0.5;1"
                dur="2.35s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.42 0 0.58 1 0.42 0 0.58 1"
              />
            </rect>
          </g>
        </>
      ) : null}
    </svg>
  );
}
